// Pure, dependency-free reducer that folds a durable SessionEvent stream into the
// tool-call CARDS the React Session view renders (foundation slice 7).
//
// vesta-repo has NO JS test runner (webpack+babel only), so the client logic lives HERE as
// a pure module asserted by a runnable node script (toolCallReducer.node-test.js) — the
// server contract is covered by rspec + e2e; the visual render is a morning check.
//
// Contract: each durable envelope is { seq, type, schema_version, payload }. A tool call is
// the TOOL_CALL_START → TOOL_CALL_ARGS → TOOL_CALL_RESULT triple correlated by
// payload.toolCallId. The reducer is order-INDEPENDENT and idempotent on seq, so live-order
// and replay-order settle to the same card list (the same "live == saved" guarantee the
// observe client proves for raw events).

// Apply one durable envelope to a cards-by-toolCallId map. Returns a NEW map (pure).
export function applyEnvelope(cardsById, envelope) {
  if (!envelope || typeof envelope !== "object") return cardsById;
  const { type, payload, seq } = envelope;
  const toolCallId = payload && payload.toolCallId;
  if (!toolCallId) return cardsById; // not a tool-call event → ignore here

  const next = { ...cardsById };
  const existing = next[toolCallId] || { toolCallId, startSeq: null, name: null, args: null, result: null };
  const card = { ...existing };

  switch (type) {
    case "TOOL_CALL_START":
      card.name = payload.toolCallName != null ? payload.toolCallName : card.name;
      // startSeq anchors card ordering; keep the FIRST start seq seen (idempotent on replay).
      if (card.startSeq == null) card.startSeq = seq;
      card.status = card.result != null ? "complete" : "running";
      break;
    case "TOOL_CALL_ARGS":
      card.args = coalesceArgs(card.args, payload.delta);
      break;
    case "TOOL_CALL_RESULT":
      card.result = payload.content != null ? payload.content : card.result;
      card.status = "complete";
      break;
    default:
      return cardsById; // non-tool-call type → unchanged
  }

  next[toolCallId] = card;
  return next;
}

// Args arrive either as one whole JSON string (durable) or as streamed partial deltas
// (ephemeral, coalesced upstream). Concatenate strings; otherwise take the latest.
function coalesceArgs(prev, delta) {
  if (delta == null) return prev;
  if (typeof prev === "string" && typeof delta === "string") return prev + delta;
  return delta;
}

// Fold a whole stream of durable envelopes into the ordered card list. Dedupe by seq so a
// replay/live overlap settles identically (deep-equal across orderings).
export function reduceToolCards(envelopes) {
  const seen = new Set();
  let cardsById = {};
  for (const env of envelopes || []) {
    if (env == null) continue;
    if (seen.has(env.seq)) continue; // idempotent on seq
    seen.add(env.seq);
    cardsById = applyEnvelope(cardsById, env);
  }
  return Object.values(cardsById).sort((a, b) => (a.startSeq || 0) - (b.startSeq || 0));
}

// Parse a card's args JSON for display (best-effort; raw string on failure).
export function parseArgs(card) {
  if (card == null || card.args == null) return null;
  if (typeof card.args !== "string") return card.args;
  try {
    return JSON.parse(card.args);
  } catch (e) {
    return card.args;
  }
}
